import { useState, useMemo } from 'react';
import type { Document, StreamItem, SourceKind } from '../../types';
import { ScreenFrame } from '../layout/ScreenFrame';
import { PageHeader } from '../layout/PageHeader';
import { SourceIcon, VisibilityChip, NavIcon } from '../primitives';
import { useDocuments } from '../../hooks/useApi';
import { relTime } from '../../utils/time';

const USER_ID = 'adam';
const SOURCE_FILTERS = ['all', 'slack', 'zoom', 'linear', 'kb', 'fathom'] as const;
type Filter = typeof SOURCE_FILTERS[number];

function docToItem(doc: Document, idx: number): StreamItem {
  const m = doc.metadata;
  const title =
    (m.title as string) ||
    (m.subject as string) ||
    (m.name as string) ||
    doc.source;

  const metaParts: string[] = [];
  if (m.channel) metaParts.push(`#${m.channel as string}`);
  if (m.author) metaParts.push(m.author as string);
  if (m.participants) metaParts.push(`${m.participants} participants`);
  if (m.duration_minutes) metaParts.push(`${m.duration_minutes} min`);

  const preview =
    (m.summary as string) ||
    (m.content as string) ||
    (m.text as string) ||
    (m.body as string) ||
    '';

  return {
    id: idx,
    src: doc.source as SourceKind,
    title,
    meta: metaParts.join(' · '),
    t: relTime(doc.occurred_at),
    preview,
    scope: doc.scope,
  };
}

function StreamRow({ item, onOpen }: { item: StreamItem; onOpen: (item: StreamItem) => void }) {
  return (
    <button
      className="stream-row"
      onClick={() => onOpen(item)}
      style={{
        width: '100%',
        textAlign: 'left',
        display: 'grid',
        gridTemplateColumns: '28px 1fr auto',
        gap: 14,
        padding: '14px 18px',
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        borderBottom: '1px solid var(--line-soft)',
        alignItems: 'flex-start',
        fontFamily: 'inherit',
      }}
    >
      <div style={{ paddingTop: 2 }}>
        <SourceIcon kind={item.src} size={18} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <div
            style={{
              fontSize: 13.5,
              fontWeight: 600,
              color: 'var(--deep)',
              letterSpacing: '-0.005em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {item.title}
          </div>
          <VisibilityChip scope={item.scope} size="sm" />
        </div>
        {item.meta && (
          <div style={{ fontSize: 12, color: 'var(--slate)', marginBottom: 4 }}>{item.meta}</div>
        )}
        {item.preview && (
          <div
            style={{
              fontSize: 12.5,
              color: 'var(--slate)',
              lineHeight: 1.5,
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {item.preview}
          </div>
        )}
      </div>
      <div
        style={{
          fontSize: 11,
          color: 'var(--slate-light)',
          fontFamily: 'var(--font-mono)',
          whiteSpace: 'nowrap',
          paddingTop: 2,
        }}
      >
        {item.t}
      </div>
    </button>
  );
}

interface Props {
  onNav: (screen: string) => void;
}

export function StreamScreen({ onNav }: Props) {
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');

  const { data, isLoading, isError, error } = useDocuments(USER_ID);

  const items = useMemo(
    () => (data ?? []).map((doc, i) => docToItem(doc, i)),
    [data],
  );

  const filtered = useMemo(() => {
    return items.filter((item) => {
      if (filter !== 'all' && item.src !== filter) return false;
      if (search && !`${item.title} ${item.preview}`.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [items, filter, search]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: items.length };
    SOURCE_FILTERS.slice(1).forEach((f) => {
      c[f] = items.filter((i) => i.src === f).length;
    });
    return c;
  }, [items]);

  return (
    <ScreenFrame>
      <PageHeader
        kicker="MVP · Step 2"
        title="Ingested data"
        meta={(
          <>
            {isLoading ? (
              <span style={{ color: 'var(--slate)' }}>Loading…</span>
            ) : (
              <span>{items.length.toLocaleString()} recent items across all sources</span>
            )}
            <span style={{ color: 'var(--line)' }}>·</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>live feed</span>
          </>
        )}
        actions={(
          <button
            className="btn-primary"
            onClick={() => onNav('ask')}
            style={{
              height: 38,
              padding: '0 16px',
              borderRadius: 'var(--radius-full)',
              background: 'var(--deep)',
              color: 'white',
              border: 'none',
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            Ask Brain
            <NavIcon name="arrow" size={14} />
          </button>
        )}
      />

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <div
          className="nav-pill-container"
          style={{
            display: 'inline-flex',
            gap: 4,
            background: 'var(--white)',
            padding: 4,
            borderRadius: 'var(--radius-full)',
            border: '1px solid var(--line)',
          }}
        >
          {SOURCE_FILTERS.map((f) => {
            const active = filter === f;
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={active ? 'nav-tab-active' : ''}
                style={{
                  border: 'none',
                  background: active ? 'var(--deep)' : 'transparent',
                  color: active ? 'white' : 'var(--slate)',
                  padding: '0 14px',
                  height: 30,
                  borderRadius: 'var(--radius-full)',
                  fontFamily: 'inherit',
                  fontSize: 12.5,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  textTransform: 'capitalize',
                }}
              >
                {f}
                <span
                  style={{
                    fontSize: 10.5,
                    padding: '1px 6px',
                    borderRadius: 4,
                    background: active ? 'rgba(255,255,255,.18)' : 'var(--surface-2)',
                    color: active ? 'white' : 'var(--slate)',
                    fontFamily: 'var(--font-mono)',
                    fontWeight: 600,
                  }}
                >
                  {counts[f] ?? 0}
                </span>
              </button>
            );
          })}
        </div>

        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            height: 38,
            padding: '0 14px',
            background: 'var(--white)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--radius-full)',
            flex: 1,
            maxWidth: 360,
          }}
        >
          <NavIcon name="search" size={14} stroke="var(--slate-light)" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter this list…"
            style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 13 }}
          />
        </div>

        {!isLoading && (
          <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--slate)' }}>
            Showing <strong style={{ color: 'var(--deep)' }}>{filtered.length}</strong> of {items.length}
          </div>
        )}
      </div>

      {/* List */}
      <div
        className="card-surface"
        style={{
          background: 'var(--white)',
          border: '1px solid var(--line)',
          borderRadius: 14,
          overflow: 'hidden',
        }}
      >
        {isLoading ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--slate)', fontSize: 13 }}>
            Loading ingested data…
          </div>
        ) : isError ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--red, #e53e3e)', fontSize: 13 }}>
            Failed to load: {(error as Error)?.message ?? 'unknown error'}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--slate)', fontSize: 13 }}>
            {items.length === 0 ? 'No data ingested yet.' : 'No items match this filter.'}
          </div>
        ) : (
          filtered.map((item) => (
            <StreamRow key={item.id} item={item} onOpen={() => {}} />
          ))
        )}
      </div>
    </ScreenFrame>
  );
}
